/**
 * The macro recorder: do it once, then do it again.
 *
 * The original's keystroke macros are a *written* list of command codes bound
 * to a function key — you type the codes into a dialog. That is useful and it
 * is not a recorder: it cannot capture where you clicked, so anything involving
 * the drawing rather than the menus is out of reach.
 *
 * This records what you actually did. Keys and clicks, in order, and replayed
 * by dispatching the same events at the same places — so whatever you can do by
 * hand replays, because the app's own handlers do the work either way. Nothing
 * here knows what a command is.
 *
 * **Clicks are remembered as offsets, not positions.** The first click of a
 * recording is the anchor and everything after it is measured from there, so
 * replaying stamps the sequence down wherever the cursor is now. A macro that
 * repeated itself into the exact spot it was recorded would put the second copy
 * on top of the first, which is a recording nobody would play twice.
 *
 * One macro, as asked for. A list of them is a different feature with a
 * management screen attached, and the original already has that for keystrokes.
 */

/** A key, as it was pressed. */
export interface MacroKey {
  t: 'key';
  key: string;
  shift: boolean;
  ctrl: boolean;
  alt: boolean;
}

/** A click, offset in inches from the first click of the recording. */
export interface MacroClick {
  t: 'click';
  dx: number;
  dy: number;
  button: number;
}

export type MacroStep = MacroKey | MacroClick;

export interface Macro {
  steps: MacroStep[];
  /** What it looks like in a menu: "6 keys, 3 clicks". */
  summary: string;
}

/**
 * How much can be recorded.
 *
 * A recording left running is the failure mode — you press record, get
 * distracted, and come back to a session's worth of keystrokes. A cap makes
 * that a message rather than a memory problem.
 */
export const MACRO_MAX = 400;

/** Describe a macro in a few words, for a menu or a status line. */
export function summarise(steps: MacroStep[]): string {
  const keys = steps.filter((s) => s.t === 'key').length;
  const clicks = steps.length - keys;
  if (steps.length === 0) return 'empty';
  const bits: string[] = [];
  if (keys > 0) bits.push(`${keys} key${keys === 1 ? '' : 's'}`);
  if (clicks > 0) bits.push(`${clicks} click${clicks === 1 ? '' : 's'}`);
  return bits.join(', ');
}

/**
 * Collects what is done while it is running.
 *
 * Deliberately dumb: it is handed events that the shell has already decided are
 * worth recording, and it does not look at the document. Deciding *what* counts
 * — not a dialog's typing, not the record command itself — belongs where those
 * things are known, which is the shell.
 */
export class MacroRecorder {
  private steps: MacroStep[] = [];
  /** The first click, which everything after it is measured from. */
  private anchor: { x: number; y: number } | null = null;
  private on = false;
  /** True once the cap was reached, so the shell can say so once. */
  full = false;

  get recording(): boolean {
    return this.on;
  }

  get length(): number {
    return this.steps.length;
  }

  start(): void {
    this.steps = [];
    this.anchor = null;
    this.full = false;
    this.on = true;
  }

  /** Stop, and hand back what was recorded. Empty means nothing was done. */
  stop(): MacroStep[] {
    this.on = false;
    return this.steps;
  }

  private room(): boolean {
    if (this.steps.length < MACRO_MAX) return true;
    this.full = true;
    return false;
  }

  key(key: string, mods: { shift?: boolean; ctrl?: boolean; alt?: boolean } = {}): void {
    if (!this.on || !this.room()) return;
    this.steps.push({
      t: 'key', key,
      shift: mods.shift === true,
      ctrl: mods.ctrl === true,
      alt: mods.alt === true,
    });
  }

  /**
   * Drop trailing keys, last first, for as long as they match.
   *
   * For the command that ends the recording. A key is recorded on the way past
   * and the menu runs the command it spells afterwards, so stopping with S R
   * leaves S and R at the end of the recording — and a macro that finishes by
   * starting another recording is not one anybody would play twice. The same
   * two letters reached by menu click leave only the S, and F11 leaves neither,
   * so this takes back what is there and no more.
   *
   * Matched without regard to case: the case of a command's second letter is
   * the difference between keeping an item and moving it, but S and R here are
   * the letters that got to this function, whichever way they were typed.
   */
  forgetTrailing(keys: readonly string[]): number {
    let dropped = 0;
    // Each in turn, taken only if it is the last step — and not stopping at the
    // first that is not. The menu route types the S and clicks the rest, so the
    // R is missing and the S behind it still has to come out.
    for (const key of keys) {
      const last = this.steps[this.steps.length - 1];
      if (last?.t !== 'key' || last.key.toLowerCase() !== key.toLowerCase()) continue;
      this.steps.pop();
      dropped++;
    }
    return dropped;
  }

  /** A click, in document coordinates. The first one sets the anchor. */
  click(x: number, y: number, button = 0): void {
    if (!this.on || !this.room()) return;
    this.anchor ??= { x, y };
    this.steps.push({
      t: 'click',
      dx: x - this.anchor.x,
      dy: y - this.anchor.y,
      button,
    });
  }
}

/**
 * Where a replayed macro sends its events, and how it knows to stop.
 *
 * An interface rather than the canvas itself so the ordering can be checked
 * without a browser — the part worth checking is that the right things happen
 * in the right order at the right places, which is not a question about the
 * DOM.
 */
export interface MacroSink {
  key(step: MacroKey): void;
  /** A click at a point in document coordinates. */
  click(x: number, y: number, button: number): void;
  /** Let whatever the last step started finish before the next one begins. */
  settle(): Promise<void>;
  /**
   * True when the macro must not go on — a dialog is waiting for an answer.
   *
   * Replay pushes events at the program as fast as it will take them, and a
   * command that stops to ask a question would otherwise be answered by
   * whatever the macro happened to press next. Stopping and saying where is the
   * only honest thing to do with the rest.
   */
  blocked(): boolean;
}

export interface MacroResult {
  /** How many steps were played. */
  played: number;
  /** The step it stopped at, when something got in the way. */
  stoppedAt?: number;
}

/**
 * Play a macro, anchored at `at`.
 *
 * `at` is where the first click lands; every later click keeps its offset from
 * it. A macro of nothing but keys ignores it entirely.
 */
export async function playMacro(
  steps: readonly MacroStep[], at: { x: number; y: number }, sink: MacroSink,
): Promise<MacroResult> {
  for (let i = 0; i < steps.length; i++) {
    if (sink.blocked()) return { played: i, stoppedAt: i };
    const step = steps[i]!;
    if (step.t === 'key') sink.key(step);
    else sink.click(at.x + step.dx, at.y + step.dy, step.button);
    await sink.settle();
  }
  return { played: steps.length };
}
